// Per-variant codec, trigger summary, and defaults; kept separate from variants.ts so the headless core stays JSX-free.

import { dateCodec, datetimeCodec, multipleCodec, rangeCodec, timeCodec } from "./codecs";
import { formatDate, type LocaleTag } from "./format";
import { isValidDate, type DateRangeValue } from "./dateCore";
import type { DateTimePickerCodec, DateTimePickerVariant, InternalValue } from "./types";

export interface VariantConfig {
  codec: DateTimePickerCodec;
  /** Renders the trigger summary. Returns "" when there is no value. */
  format: (value: InternalValue, pattern: string, locale: LocaleTag) => string;
  defaultFormat: string;
  defaultPlaceholder: string;
  /** Whether picking a day should close the popover. */
  closeOnSelect: boolean;
  /** Whether this variant shows a calendar grid at all. */
  hasCalendar: boolean;
  hasTime: boolean;
}
/* Trigger summaries                                                           */

function formatSingle(value: InternalValue, pattern: string, locale: LocaleTag): string {
  return isValidDate(value) ? formatDate(value, pattern, locale) : "";
}

function formatRange(value: InternalValue, pattern: string, locale: LocaleTag): string {
  const range = value as DateRangeValue | undefined;
  if (!isValidDate(range?.from)) return "";
  const from = formatDate(range.from, pattern, locale);
  // An open-ended range shows as "from -", so a half-finished pick reads as such.
  if (!isValidDate(range.to)) return `${from} -`;
  return `${from} - ${formatDate(range.to, pattern, locale)}`;
}

/** Beyond this many days, listing them all would overflow the trigger. */
const MULTIPLE_SUMMARY_THRESHOLD = 3;

function formatMultiple(value: InternalValue, pattern: string, locale: LocaleTag): string {
  if (!Array.isArray(value) || value.length === 0) return "";
  const dates = (value as Date[]).filter((entry) => isValidDate(entry));
  if (dates.length === 0) return "";
  if (dates.length <= MULTIPLE_SUMMARY_THRESHOLD) {
    return dates.map((entry) => formatDate(entry, pattern, locale)).join(", ");
  }
  return `${dates.length} dates selected`;
}

function formatTimeValue(value: InternalValue): string {
  return typeof value === "string" ? value : "";
}

/* Registry                                                                    */

export const VARIANT_CONFIG: Record<DateTimePickerVariant, VariantConfig> = {
  single: {
    codec: dateCodec,
    format: formatSingle,
    defaultFormat: "dd/MM/yyyy",
    defaultPlaceholder: "Select date",
    closeOnSelect: true,
    hasCalendar: true,
    hasTime: false,
  },
  range: {
    codec: rangeCodec,
    format: formatRange,
    defaultFormat: "dd/MM/yyyy",
    defaultPlaceholder: "Select date range",
    closeOnSelect: true,
    hasCalendar: true,
    hasTime: false,
  },
  multiple: {
    codec: multipleCodec,
    format: formatMultiple,
    defaultFormat: "dd/MM/yyyy",
    defaultPlaceholder: "Select dates",
    // Picking one day of several must not dismiss the calendar.
    closeOnSelect: false,
    hasCalendar: true,
    hasTime: false,
  },
  time: {
    codec: timeCodec,
    format: formatTimeValue,
    defaultFormat: "HH:mm",
    defaultPlaceholder: "Select time",
    closeOnSelect: false,
    hasCalendar: false,
    hasTime: true,
  },
  /* Identical wire behaviour to `time`; only the panel differs. */
  "time-stepper": {
    codec: timeCodec,
    format: formatTimeValue,
    defaultFormat: "HH:mm",
    defaultPlaceholder: "Select time",
    closeOnSelect: false,
    hasCalendar: false,
    hasTime: true,
  },
  datetime: {
    codec: datetimeCodec,
    format: formatSingle,
    defaultFormat: "dd/MM/yyyy HH:mm",
    defaultPlaceholder: "Select date and time",
    // The day is only half the value -- closing here would strand the time.
    closeOnSelect: false,
    hasCalendar: true,
    hasTime: true,
  },
};
